import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { ArrowDownRight, ArrowUpRight, Car, Factory, Leaf, TrendingUp } from 'lucide-react';
import { useEmissionsSummary } from '@/api/hooks/useEmissions';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { ReductionLevers } from '@/components/emissions/ReductionLevers';

const NUM = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 0 });
const FLOAT2 = new Intl.NumberFormat('en-ZA', { maximumFractionDigits: 2 });

export default function Emissions() {
  const { data, isLoading, error } = useEmissionsSummary();

  return (
    <div className="flex h-full flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Emissions</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Scope-1 fleet CO2 derived from fuel volume — executive view.
        </p>
      </div>

      {error && (
        <Card className="border-red-200 bg-red-50">
          <CardContent className="py-3 text-sm text-red-800">
            Could not load emissions data: {(error as Error).message}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi
          title="CO2 (last 12 months)"
          icon={<Leaf className="h-4 w-4 text-muted-foreground" />}
          value={data ? `${NUM.format(data.kpis.total_tonnes_12m)} tCO2` : null}
          loading={isLoading}
        />
        <Kpi
          title="CO2 year-to-date"
          icon={<TrendingUp className="h-4 w-4 text-muted-foreground" />}
          value={data ? `${NUM.format(data.kpis.total_tonnes_ytd)} tCO2` : null}
          loading={isLoading}
          delta={data?.kpis.yoy_pct ?? null}
          deltaLabel="vs LY same window"
        />
        <Kpi
          title="Active vehicles (12m)"
          icon={<Car className="h-4 w-4 text-muted-foreground" />}
          value={data ? NUM.format(data.kpis.active_vehicles) : null}
          loading={isLoading}
        />
        <Kpi
          title="Intensity"
          icon={<Factory className="h-4 w-4 text-muted-foreground" />}
          value={
            data?.kpis.tonnes_per_active_vehicle_12m != null
              ? `${FLOAT2.format(data.kpis.tonnes_per_active_vehicle_12m)} tCO2/veh`
              : data
                ? '—'
                : null
          }
          loading={isLoading}
          subtle="Tonnes per active vehicle (12m)"
        />
      </div>

      {data && (
        <p className="text-xs text-muted-foreground">
          CO2 computed at {FLOAT2.format(data.co2_factor_kg_per_litre)} kg/L. {data.co2_factor_note}
        </p>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm">Monthly CO2 (last 24 months)</CardTitle>
        </CardHeader>
        <CardContent className="h-72 px-2 py-2">
          {isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={data?.monthly_trend ?? []}
                margin={{ top: 4, right: 8, bottom: 4, left: 0 }}
              >
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v: string) => v.slice(0, 7)}
                />
                <YAxis
                  tick={{ fontSize: 10 }}
                  width={60}
                  tickFormatter={(v: number) => NUM.format(v)}
                />
                <Tooltip
                  formatter={(v: number) => `${NUM.format(v)} tCO2`}
                  labelFormatter={(v: string) => `Month: ${v.slice(0, 7)}`}
                />
                <Line
                  type="monotone"
                  dataKey="tonnes_co2"
                  name="tCO2"
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Top 10 cost centres by CO2 (last 12 months)</CardTitle>
          </CardHeader>
          {/* Cost-centre labels are long ("NAT HEAD OFFICE-CONTR. SUBCOMPONENT
              (000003001741)"). At h-80 each band was ~32px while the 110px
              gutter wrapped every label onto 4-5 lines, so they overlapped into
              an unreadable block. Taller bands + a wider single-line gutter with
              ellipsis; the full label is in the tooltip. */}
          <CardContent className="h-[30rem] px-2 py-2">
            {isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data?.top_cost_centres ?? []}
                  layout="vertical"
                  margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
                >
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v: number) => NUM.format(v)}
                  />
                  <YAxis
                    type="category"
                    dataKey="label"
                    tick={{ fontSize: 11 }}
                    width={215}
                    interval={0}
                    tickFormatter={(v: string) =>
                      v.length > 28 ? `${v.slice(0, 27)}…` : v
                    }
                  />
                  <Tooltip
                    formatter={(v: number) => `${NUM.format(v)} tCO2`}
                    labelFormatter={(l: string) => l}
                  />
                  <Bar dataKey="tonnes_co2" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Top 10 makes by CO2 (last 12 months)</CardTitle>
          </CardHeader>
          <CardContent className="h-[30rem] px-2 py-2">
            {isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data?.top_makes ?? []}
                  layout="vertical"
                  margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
                >
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="3 3" />
                  <XAxis
                    type="number"
                    tick={{ fontSize: 10 }}
                    tickFormatter={(v: number) => NUM.format(v)}
                  />
                  <YAxis
                    type="category"
                    dataKey="make"
                    tick={{ fontSize: 11 }}
                    width={120}
                    interval={0}
                  />
                  <Tooltip formatter={(v: number) => `${NUM.format(v)} tCO2`} />
                  <Bar dataKey="tonnes_co2" fill="hsl(var(--primary))" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
      </div>

      <ReductionLevers />
    </div>
  );
}

interface KpiProps {
  title: string;
  value: string | null;
  icon: React.ReactNode;
  loading: boolean;
  delta?: number | null;
  deltaLabel?: string;
  subtle?: string;
}

function Kpi({ title, value, icon, loading, delta, deltaLabel, subtle }: KpiProps) {
  const deltaColor =
    delta == null
      ? 'text-muted-foreground'
      : delta <= 0
        ? 'text-emerald-700'
        : 'text-red-700';
  const DeltaIcon = delta != null && delta <= 0 ? ArrowDownRight : ArrowUpRight;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-1.5 pt-3">
        <CardTitle className="text-xs font-medium text-muted-foreground">
          {title}
        </CardTitle>
        {icon}
      </CardHeader>
      <CardContent className="pb-3 pt-0">
        {loading ? (
          <Skeleton className="h-7 w-24" />
        ) : (
          <div className="text-2xl font-semibold tabular-nums">{value ?? '—'}</div>
        )}
        {delta != null && (
          <div className={`mt-1 flex items-center gap-1 text-xs ${deltaColor}`}>
            <DeltaIcon className="h-3 w-3" />
            <span className="font-medium">{Math.abs(delta).toFixed(1)}%</span>
            {deltaLabel && <span className="text-muted-foreground">{deltaLabel}</span>}
          </div>
        )}
        {subtle && delta == null && (
          <div className="mt-1 text-xs text-muted-foreground">{subtle}</div>
        )}
      </CardContent>
    </Card>
  );
}
